/**
 * Shared resize handle for the right-side panel (drawer OR list view).
 *
 * Behaviour mirrors the version that was previously private to Drawer.tsx:
 *  - Fixed-position vertical strip at the boundary between the canvas
 *    pane and the panel pane. Width is `drawerWidth` from the store.
 *  - On pointerdown → start a drag. window-level pointermove updates a
 *    liveWidthRef in real time. A RAF callback applies the live width
 *    to the panel's DOM elements directly via `applyLiveWidth`, so the
 *    drag is bound to the browser's paint rate rather than React's
 *    reconcile rate. Zustand is updated exactly once on pointerup.
 *  - When closed, the handle sits off-screen at right: -20 so it
 *    doesn't intercept canvas hover.
 *
 * Each panel passes its own `applyLiveWidth` so the handle stays
 * decoupled from a specific panel structure (drawer has aside + inner
 * to mutate; list view has a single container). The handle reads
 * drawerWidth / setDrawerWidth / drawerResizing from the store
 * directly so consumers don't need to thread that state through.
 */
import { useEffect, useRef, useState } from 'react';
import {
  DRAWER_MAX_WIDTH,
  DRAWER_MIN_WIDTH,
  SHEET_MIN_HEIGHT,
  useAppStore,
} from '../state/store';
import { useViewport } from './useViewport';

// CSS for the handle's visual line + hover/dragging states. Two
// orientations now: vertical bar at the canvas/side-panel boundary
// (horizontal-axis resize), and horizontal bar at the canvas/sheet
// boundary on mobile portrait (vertical-axis resize). The bars
// share the dim-line aesthetic that brightens on hover/drag.
const STYLES = `
.bjjmap-resize-handle {
  position: fixed;
  z-index: 60;
  user-select: none;
  touch-action: none;
}
.bjjmap-resize-handle.h {
  /* Horizontal-axis (width) resize — vertical bar at the boundary. */
  top: 0;
  bottom: 0;
  width: 10px;
  cursor: ew-resize;
}
.bjjmap-resize-handle.v {
  /* Vertical-axis (height) resize — horizontal bar at the boundary
     between the canvas and the bottom sheet on mobile portrait.
     Tall enough (28px) that a thumb has a reasonable hit target on
     a phone screen — the visible line is still 1px so the handle
     reads as a hair-line, but the touch area extends well above and
     below it. */
  left: 0;
  right: 0;
  height: 28px;
  cursor: ns-resize;
}
.bjjmap-resize-handle::before {
  content: '';
  position: absolute;
  background: rgba(255,255,255,0.06);
  transition: background 120ms ease, width 120ms ease, height 120ms ease, top 120ms ease, left 120ms ease;
}
.bjjmap-resize-handle.h::before {
  left: 4.5px;
  top: 0;
  bottom: 0;
  width: 1px;
}
.bjjmap-resize-handle.v::before {
  /* Visible line is centered vertically in the 28px touch area
     (14 - 0.5 = 13.5). */
  top: 13.5px;
  left: 0;
  right: 0;
  height: 1px;
}
.bjjmap-resize-handle:hover::before,
.bjjmap-resize-handle.dragging::before {
  background: rgba(202,211,223,0.45);
}
.bjjmap-resize-handle.h:hover::before,
.bjjmap-resize-handle.h.dragging::before {
  width: 2px;
  left: 4px;
}
.bjjmap-resize-handle.v:hover::before,
.bjjmap-resize-handle.v.dragging::before {
  height: 2px;
  top: 13px;
}
`;
let stylesInjected = false;
function ensureStyles() {
  if (typeof document === 'undefined' || stylesInjected) return;
  const tag = document.createElement('style');
  tag.dataset.bjjmap = 'resize-handle-styles';
  tag.textContent = STYLES;
  document.head.appendChild(tag);
  stylesInjected = true;
}

type Props = {
  open: boolean;
  /** Mutate the panel DOM with the live size during a drag — width in
   *  horizontal mode (side drawer), height in vertical mode (bottom
   *  sheet on mobile portrait). The `axis` arg tells the consumer
   *  which dimension to apply so the same callback works for both.
   *  Called on every animation frame while pointermove is active.
   *  Kept named applyLiveWidth for backwards compatibility with the
   *  three panel call-sites; the second arg is optional and defaults
   *  to 'width' so consumers that pre-date the bottom-sheet mode
   *  still type-check. */
  applyLiveWidth: (sizePx: number, axis?: 'width' | 'height') => void;
};

export function ResizeHandle({ open, applyLiveWidth }: Props) {
  useEffect(() => {
    ensureStyles();
  }, []);
  const drawerWidth = useAppStore((s) => s.drawerWidth);
  const setDrawerWidth = useAppStore((s) => s.setDrawerWidth);
  const sheetHeight = useAppStore((s) => s.sheetHeight);
  const setSheetHeight = useAppStore((s) => s.setSheetHeight);
  const setDrawerResizing = useAppStore((s) => s.setDrawerResizing);
  const drawerResizing = useAppStore((s) => s.drawerResizing);
  const { isMobile, isPortrait } = useViewport();
  // Vertical mode = drag the bottom sheet's top edge to resize its
  // height. Triggered by the same viewport classification that flips
  // the panel into bottom-sheet mode in useResponsivePanelLayout.
  const vertical = isMobile && isPortrait;

  const [dragging, setDragging] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);
  // The live value the drag is mutating. Width for horizontal mode,
  // height for vertical mode — we just rename the variable.
  const startSizeRef = useRef(vertical ? sheetHeight : drawerWidth);
  const startCoordRef = useRef(0);
  const liveSizeRef = useRef(vertical ? sheetHeight : drawerWidth);
  const rafRef = useRef<number | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const baseSize = vertical ? sheetHeight : drawerWidth;
    startSizeRef.current = baseSize;
    liveSizeRef.current = baseSize;
    startCoordRef.current = vertical ? e.clientY : e.clientX;
    setDragging(true);
    setDrawerResizing(true);

    const onFrame = () => {
      rafRef.current = null;
      const v = liveSizeRef.current;
      // Mutate the panel DOM in BOTH modes so the canvas pane reflows
      // (via flexbox) in lockstep with the pointer and R3F's
      // ResizeObserver re-fires the camera-aspect update each frame.
      applyLiveWidth(v, vertical ? 'height' : 'width');
      if (vertical) {
        if (handleRef.current) {
          // Handle is 28px tall in vertical mode (see CSS .v rule);
          // we anchor its bottom edge 14px BELOW the sheet's top so
          // the visible 1px line lands exactly on the sheet boundary
          // and the touch area extends 14px above and 14px below it.
          handleRef.current.style.bottom = `${v - 14}px`;
        }
        if (typeof document !== 'undefined') {
          // Drives PanelHandle's caret so it slides with the sheet
          // boundary as the pointer moves.
          document.documentElement.style.setProperty(
            '--bjjmap-sheet-height',
            `${v}px`,
          );
        }
      } else {
        if (handleRef.current) handleRef.current.style.right = `${v - 5}px`;
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty(
            '--bjjmap-drawer-width',
            `${v}px`,
          );
        }
      }
    };

    const onMove = (ev: PointerEvent) => {
      if (vertical) {
        // Moving the handle UP (negative dy) grows the sheet; DOWN
        // shrinks it. Clamp to a runtime-safe maximum (85% viewport).
        const dy = ev.clientY - startCoordRef.current;
        const raw = startSizeRef.current - dy;
        const maxH = Math.floor(window.innerHeight * 0.85);
        liveSizeRef.current = Math.max(
          SHEET_MIN_HEIGHT,
          Math.min(maxH, raw),
        );
      } else {
        const dx = ev.clientX - startCoordRef.current;
        const raw = startSizeRef.current - dx;
        liveSizeRef.current = Math.max(
          DRAWER_MIN_WIDTH,
          Math.min(DRAWER_MAX_WIDTH, raw),
        );
      }
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(onFrame);
      }
    };
    const onUp = () => {
      setDragging(false);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Commit the final value to the store once — single update for
      // the whole drag interaction.
      if (vertical) {
        setSheetHeight(liveSizeRef.current);
      } else {
        setDrawerWidth(liveSizeRef.current);
      }
      setDrawerResizing(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
    };

    document.body.style.cursor = vertical ? 'ns-resize' : 'ew-resize';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  // Position the handle at the boundary appropriate to the mode.
  // Width / horizontal: cling to the panel's left edge (right: width).
  // Height / vertical: cling to the sheet's top edge (bottom: height).
  const positionStyle = vertical
    ? {
        // Anchor handle bottom 14px below the sheet's top edge so
        // the (28px-tall) hit area straddles the boundary equally
        // above and below; the visible 1px line lands ON the
        // boundary. Matches the per-frame update inside onFrame.
        bottom: open ? sheetHeight - 14 : -40,
        transition: drawerResizing
          ? 'none'
          : 'bottom 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        willChange: 'bottom' as const,
      }
    : {
        right: open ? drawerWidth - 5 : -20,
        transition: drawerResizing
          ? 'none'
          : 'right 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        willChange: 'right' as const,
      };

  return (
    <div
      ref={handleRef}
      className={`bjjmap-resize-handle ${vertical ? 'v' : 'h'}${
        dragging ? ' dragging' : ''
      }`}
      style={positionStyle}
      onPointerDown={onPointerDown}
      role="separator"
      aria-orientation={vertical ? 'horizontal' : 'vertical'}
      aria-label="Resize panel"
    />
  );
}
